// client/src/context/CartContext.js
import React, { createContext, useState, useEffect } from 'react';
import { Snackbar, Paper, Stack, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getProductImageUrl } from '../utils/media';

export const CartContext = createContext({
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  getCartTotal: () => 0,
});

const cartKeyFor = (productId, variantId) => (variantId ? `${productId}-v${variantId}` : `${productId}`);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const localData = localStorage.getItem('cartItems');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      console.error("Could not parse cart items from localStorage", error);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  const [toast, setToast] = useState(null);

  // `variant`, if provided, is { id, color, size, price, image_url, quantity }
  const addToCart = (product, quantity = 1, variant = null) => {
    const cartKey = cartKeyFor(product.id, variant?.id);
    setToast({ name: product.name, imageUrl: (variant && variant.image_url) || product.imageUrl, key: Date.now() });
    setCartItems(prevItems => {
      const itemExists = prevItems.find(item => item.cartKey === cartKey);
      if (itemExists) {
        return prevItems.map(item =>
          item.cartKey === cartKey ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      const variantLabel = variant ? [variant.color, variant.size].filter(Boolean).join(' / ') : null;
      return [...prevItems, {
        cartKey,
        id: product.id,
        name: product.name,
        imageUrl: (variant && variant.image_url) || product.imageUrl,
        price: variant && variant.price != null ? variant.price : product.price,
        quantity,
        variantId: variant ? variant.id : null,
        variantLabel,
      }];
    });
  };

  const removeFromCart = (cartKey) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    const newQuantity = Math.max(1, quantity); // Ensure quantity is at least 1
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.cartKey === cartKey ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal }}>
      {children}
      <Snackbar
        key={toast?.key}
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Paper elevation={0} sx={{ p: 1.5, pr: 2.5, borderRadius: 3, border: 1, borderColor: 'divider', minWidth: 300 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {toast?.imageUrl && (
              <Box component="img" src={getProductImageUrl(toast.imageUrl)} alt="" sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 2, bgcolor: 'action.hover' }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'success.main' }}>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="body2" fontWeight={700}>Added to cart</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 240 }}>{toast?.name}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Snackbar>
    </CartContext.Provider>
  );
};
